import {useReducer} from 'react';

import {isNil} from 'ramda';

import {isDefined} from 'shared/web';

import {PARALLEL_UPLOADS} from '../constants/parallelUploads';
import {GetChunkFn} from '../types/GetChunkFn';
import {HandleMultiMultiPartUploadType} from '../types/HandleMultiMultiPartUploadType';
import {QueueVideoTask} from '../types/QueueVideoTask';
import {QueueVideoTaskJob} from '../types/QueueVideoTaskJob';
import {UploadApi} from '../types/UploadApi';
import {VideoUploadError} from '../types/VideoUploadError';
import {convertFileToQueueTask} from '../utils/convertFileToQueueTask';
import {enhanceWithCountOfParts} from '../utils/enhanceWithCountOfParts';
import {getUploadProgress} from '../utils/getUploadProgress';
import {handleUploadChunk} from '../utils/handleUploadChunk';
import {TaskActionKind as ActionKind, taskReducer} from '../utils/taskReducer';
import {usePreSignLinksManager} from './usePreSignLinksManager';
import {useVideoCompleteManager} from './useVideoCompleteManager';

export function useVideoUploadManager(
  api: UploadApi,
  errorCallback: (videoUploadError: VideoUploadError, error?: Error) => void
) {
  const [{tasks}, dispatch] = useReducer(taskReducer, {tasks: []});
  const {getPreSignLinks} = usePreSignLinksManager(api.sendMultiPartUploadRequest);
  const {handleTaskCompleted} = useVideoCompleteManager(api.sendMultipartUploadCompleted);

  const addTask = (file: QueueVideoTask) =>
    dispatch({type: ActionKind.ADD_TASK, payload: {task: file}});

  const removeTask = (id: string) => dispatch({type: ActionKind.REMOVE_TASK, payload: {id}});

  const handleError = (
    videoUploadError: VideoUploadError,
    taskConnectionFileId?: string,
    error?: Error
  ) => {
    errorCallback(videoUploadError, error);
    if (isDefined(taskConnectionFileId)) {
      updateTaskStatus(taskConnectionFileId, 'ERROR');
    }
  };

  /**
   * Retrieves the progress of each task.
   */
  const uploadProgress = tasks
    .map((task) => {
      const doneJobsLength = task.jobs.filter((j) => j.status === 'DONE').length;
      const allJobsLength = task.jobs.length;

      return allJobsLength > 0
        ? {
            id: task.id,
            progress: getUploadProgress(doneJobsLength / allJobsLength),
            connectionFileId: task.connectionFileId,
          }
        : null;
    })
    .filter(isDefined);

  /**
   * Initiates a multipart upload process for a file.
   */
  const handleMultiMultiPartUpload: HandleMultiMultiPartUploadType = async (
    rawFile,
    callback,
    onError
  ) => {
    const file = enhanceWithCountOfParts(rawFile);
    const chunkSize = file.chunkSize;

    if (isNil(chunkSize)) {
      return Promise.resolve(errorCallback(VideoUploadError.MAX_VIDEO_SIZE));
    }

    try {
      const uploadInfo = await getPreSignLinks(file);
      const task = convertFileToQueueTask({...file, chunkSize}, uploadInfo, callback, onError);
      addTask(task);

      return task;
    } catch {
      return errorCallback(VideoUploadError.GET_PRE_SIGN_LINKS);
    }
  };

  const handleExpiredTask = (task: QueueVideoTask) => {
    removeTask(task.id);
    return handleMultiMultiPartUpload(task, task.callback);
  };

  /**
   * Updates an existing task status in the upload queue with new information.
   */
  const updateTaskStatus = (taskConnectionFileId: string, status: QueueVideoTask['status']) =>
    dispatch({type: ActionKind.UPDATE_TASK_STATUS, payload: {status, taskConnectionFileId}});

  /**
   * Updates a specific job within an upload task.
   */
  const updateTaskJob = (
    taskConnectionFileId: string,
    updatedJob: Partial<QueueVideoTaskJob> & {id: string}
  ) =>
    dispatch({type: ActionKind.UPDATE_TASK_JOB, payload: {job: updatedJob, taskConnectionFileId}});

  /**
   * Processes a single job within an upload task by uploading the corresponding chunk of the file.
   * It updates the job's status to 'ONGOING' at the start and to 'DONE' upon completion.
   */
  const processJob = (job: QueueVideoTaskJob, task: QueueVideoTask, getChunk: GetChunkFn) => {
    updateTaskJob(task.connectionFileId, {id: job.id, status: 'ONGOING'});

    return handleUploadChunk(task, job, getChunk)
      .then(({etag}) => updateTaskJob(task.connectionFileId, {id: job.id, status: 'DONE', etag}))
      .catch((error: Error) => {
        // Reset to PENDING so the job is retried on the next processTask cycle.
        // Leaving it ONGOING would permanently block processTask (hasOngoingJobs guard).
        updateTaskJob(task.connectionFileId, {id: job.id, status: 'PENDING'});
        handleError(VideoUploadError.UNABLE_TO_UPLOAD_CHUNK, task.connectionFileId, error);
        task.onError?.(task);
      });
  };

  /**
   * Initiates the processing of an upload task, running up to PARALLEL_UPLOADS jobs concurrently.
   * The effect loop in VideoUploadQueueProvider drives repeated calls via the PENDING status toggle.
   */
  const processTask = (
    task: QueueVideoTask,
    getChunk: GetChunkFn,
    onCompleteCallback: (id: string) => void
  ) => {
    if (task.status === 'PENDING') {
      updateTaskStatus(task.connectionFileId, 'ONGOING');
    }

    const pendingJobs = task.jobs.filter((job) => job.status === 'PENDING');
    const hasOngoingJobs = task.jobs.some((job) => job.status === 'ONGOING');

    if (pendingJobs.length > 0) {
      const batch = pendingJobs.slice(0, PARALLEL_UPLOADS);
      return Promise.all(batch.map((job) => processJob(job, task, getChunk))).finally(() =>
        updateTaskStatus(task.connectionFileId, 'PENDING')
      );
    }

    if (hasOngoingJobs) {
      return;
    }

    updateTaskStatus(task.connectionFileId, 'DONE');
    onCompleteCallback(task.id);

    return handleTaskCompleted(task.connectionFileId, task.jobs)
      .then(() => task.callback(task))
      .then(() => removeTask(task.id))
      .catch((error: Error) => {
        handleError(VideoUploadError.UNABLE_TO_COMPLETE_TASK, task.connectionFileId, error);
        task.onError?.(task);
      });
  };

  return {
    tasks,
    removeTask,
    processTask,
    uploadProgress,
    handleExpiredTask,
    handleMultiMultiPartUpload,
  };
}
